/**
 * Store Zustand du funnel "Diagnostic CEE agricole".
 *
 * Flow DYNAMIQUE : la séquence d'étapes s'adapte aux réponses de l'utilisateur.
 *
 *  Étapes fixes (toujours) :  orientation → siret → infra-overview → travaux → contact → result
 *  Étapes insérées dynamiquement entre `infra-overview` et `travaux` selon
 *  les sections déclarées à `infra-overview` :  serres ? elevage ? stockage ?
 *
 *  Profil minimum (aucune infra) :  6 étapes visibles
 *  Profil maximum (toutes infras) :  9 étapes visibles
 *
 * Persistance localStorage : reprise après F5.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ElevageType,
  OrientationId,
  SerresChauffageCombustible,
  SerresCouverture,
  SerresType,
  StockageUsage,
  EquipementId,
} from './agri-config';

/* ─── Types ──────────────────────────────────────────────── */

/** ID stable d'une étape (utilisé pour le routing dynamique). */
export type AgriStepId =
  | 'orientation'
  | 'siret'
  | 'infra-overview'
  | 'serres'
  | 'elevage'
  | 'stockage'
  | 'travaux'
  | 'contact'
  | 'result';

/** Liste de toutes les étapes possibles (ordre canonique). */
export const ALL_STEP_IDS: AgriStepId[] = [
  'orientation',
  'siret',
  'infra-overview',
  'serres',
  'elevage',
  'stockage',
  'travaux',
  'contact',
  'result',
];

/** Libellés courts pour le stepper visuel. */
export const AGRI_STEP_LABELS: Record<AgriStepId, string> = {
  orientation: 'Orientation',
  siret: 'SIRET',
  'infra-overview': 'Installations',
  serres: 'Serres',
  elevage: 'Élevage',
  stockage: 'Stockage',
  travaux: 'Travaux',
  contact: 'Contact',
  result: 'Résultat',
};

export interface SerresInfo {
  has: boolean | null;
  type: SerresType | null;
  couvertures: SerresCouverture[];
  chauffee: boolean | null;
  combustible: SerresChauffageCombustible | null;
  surfaceM2: number | null;
  regulationInformatisee: boolean | null;
}

export interface ElevageInfo {
  has: boolean | null;
  types: ElevageType[];
  effectif: number | null;
  hasTank: boolean | null;
  hasSalleTraite: boolean | null;
}

export interface StockageInfo {
  has: boolean | null;
  usages: StockageUsage[];
  refrigere: boolean | null;
  surfaceM2: number | null;
}

export interface ContactInfo {
  firstName: string;
  lastName: string;
  phoneCountry: string;
  phoneDial: string;
  phoneNational: string;
  email: string;
  address: string;
  postalCode: string;
}

export interface CompanyInfo {
  siret: string;
  name: string;
  naf: string | null;
  segment: string;
}

export interface AgriFunnelState {
  stepId: AgriStepId;
  dir: 1 | -1;

  /* — Step 1 : orientation — */
  orientation: OrientationId | null;

  /* — Step 2 : SIRET / Entreprise — */
  company: CompanyInfo | null;

  /* — Step 3-5 : infrastructures — */
  serres: SerresInfo;
  elevage: ElevageInfo;
  stockage: StockageInfo;

  /* — Step 6 : équipements complémentaires + travaux — */
  equipements: EquipementId[];
  travaux: string[];

  /* — Step 7 : contact — */
  contact: ContactInfo;

  /* — Step 8 : résultat — */
  leadId: string | null;
  submitting: boolean;
  submitError: string | null;
}

interface AgriFunnelActions {
  setStep: (stepId: AgriStepId, dir?: 1 | -1) => void;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;

  setOrientation: (o: OrientationId) => void;
  setCompany: (c: CompanyInfo) => void;

  patchSerres: (patch: Partial<SerresInfo>) => void;
  patchElevage: (patch: Partial<ElevageInfo>) => void;
  patchStockage: (patch: Partial<StockageInfo>) => void;

  toggleEquipement: (id: EquipementId) => void;
  toggleTravail: (id: string) => void;
  setTravaux: (ids: string[]) => void;

  patchContact: (patch: Partial<ContactInfo>) => void;

  setSubmitting: (v: boolean) => void;
  setSubmitError: (msg: string | null) => void;
  setLeadId: (id: string | null) => void;
}

const INITIAL_SERRES: SerresInfo = {
  has: null,
  type: null,
  couvertures: [],
  chauffee: null,
  combustible: null,
  surfaceM2: null,
  regulationInformatisee: null,
};

const INITIAL_ELEVAGE: ElevageInfo = {
  has: null,
  types: [],
  effectif: null,
  hasTank: null,
  hasSalleTraite: null,
};

const INITIAL_STOCKAGE: StockageInfo = {
  has: null,
  usages: [],
  refrigere: null,
  surfaceM2: null,
};

const INITIAL_CONTACT: ContactInfo = {
  firstName: '',
  lastName: '',
  phoneCountry: 'FR',
  phoneDial: '33',
  phoneNational: '',
  email: '',
  address: '',
  postalCode: '',
};

const INITIAL_STATE: AgriFunnelState = {
  stepId: 'orientation',
  dir: 1,
  orientation: null,
  company: null,
  serres: INITIAL_SERRES,
  elevage: INITIAL_ELEVAGE,
  stockage: INITIAL_STOCKAGE,
  equipements: [],
  travaux: [],
  contact: INITIAL_CONTACT,
  leadId: null,
  submitting: false,
  submitError: null,
};

/* ─── Routing dynamique ──────────────────────────────────── */

/**
 * Renvoie la séquence des étapes visibles selon le state actuel.
 *
 * - serres / elevage / stockage : inclus uniquement si `has === true`
 * - les autres étapes sont toujours présentes
 */
export function getActiveSteps(state: AgriFunnelState): AgriStepId[] {
  return ALL_STEP_IDS.filter((id) => {
    if (id === 'serres') return state.serres.has === true;
    if (id === 'elevage') return state.elevage.has === true;
    if (id === 'stockage') return state.stockage.has === true;
    return true;
  });
}

/** Index 0-based de l'étape courante dans la séquence visible. */
export function getActiveIndex(state: AgriFunnelState): number {
  const list = getActiveSteps(state);
  const idx = list.indexOf(state.stepId);
  return idx === -1 ? 0 : idx;
}

/**
 * Libellé « Étape X sur Y » dynamique — le funnel compte 6 à 9 étapes
 * selon les infrastructures déclarées (serres, élevage, stockage).
 */
export function agriStepCounterLabel(state: AgriFunnelState): string {
  const list = getActiveSteps(state);
  return `Étape ${getActiveIndex(state) + 1} sur ${list.length}`;
}

/** Calcule l'étape suivante en respectant le routing dynamique. */
function computeNextStep(state: AgriFunnelState): AgriStepId {
  const list = getActiveSteps(state);
  const idx = list.indexOf(state.stepId);
  return list[Math.min(list.length - 1, idx + 1)] ?? state.stepId;
}

/** Calcule l'étape précédente en respectant le routing dynamique. */
function computePrevStep(state: AgriFunnelState): AgriStepId {
  const list = getActiveSteps(state);
  const idx = list.indexOf(state.stepId);
  return list[Math.max(0, idx - 1)] ?? state.stepId;
}

/* ─── Store ──────────────────────────────────────────────── */

const STORAGE_KEY = 'sef_agri_diagnostic_v2';

export const useAgriStore = create<AgriFunnelState & AgriFunnelActions>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setStep: (stepId, dir) =>
        set((s) => ({
          stepId,
          dir:
            dir ??
            (getActiveSteps(s).indexOf(stepId) > getActiveSteps(s).indexOf(s.stepId)
              ? 1
              : -1),
        })),

      goNext: () => {
        const state = get();
        const next = computeNextStep(state);
        set({ stepId: next, dir: 1 });
      },

      goBack: () => {
        const state = get();
        const prev = computePrevStep(state);
        set({ stepId: prev, dir: -1 });
      },

      reset: () => set({ ...INITIAL_STATE }),

      setOrientation: (o) => set({ orientation: o }),

      setCompany: (c) => set({ company: c }),

      patchSerres: (patch) => set((s) => ({ serres: { ...s.serres, ...patch } })),
      patchElevage: (patch) => set((s) => ({ elevage: { ...s.elevage, ...patch } })),
      patchStockage: (patch) => set((s) => ({ stockage: { ...s.stockage, ...patch } })),

      toggleEquipement: (id) =>
        set((s) => {
          if (s.equipements.includes(id)) {
            return { equipements: s.equipements.filter((x) => x !== id) };
          }
          if (id === 'aucun') return { equipements: ['aucun'] };
          return {
            equipements: [...s.equipements.filter((x) => x !== 'aucun'), id],
          };
        }),

      toggleTravail: (id) =>
        set((s) => {
          if (s.travaux.includes(id)) {
            return { travaux: s.travaux.filter((x) => x !== id) };
          }
          return { travaux: [...s.travaux, id] };
        }),

      setTravaux: (ids) => set({ travaux: ids }),

      patchContact: (patch) => set((s) => ({ contact: { ...s.contact, ...patch } })),

      setSubmitting: (v) => set({ submitting: v }),
      setSubmitError: (msg) => set({ submitError: msg }),
      setLeadId: (id) => set({ leadId: id }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({
        stepId: s.stepId,
        orientation: s.orientation,
        company: s.company,
        serres: s.serres,
        elevage: s.elevage,
        stockage: s.stockage,
        equipements: s.equipements,
        travaux: s.travaux,
        contact: s.contact,
      }),
    },
  ),
);
